/**
 * Accessible dialog listing simulation questions/tasks with answered state and jump actions.
 *
 * @module SimulationQuestionNavigatorDialog
 */

import { useTranslation } from "react-i18next";
import { ListChecks } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SimulationNavItem } from "@/utils/simulationQuestionNavigation";

interface SimulationQuestionNavigatorDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly items: SimulationNavItem[];
  readonly onJump: (itemId: string) => void;
  readonly jumpEnabled: boolean;
}

export function SimulationQuestionNavigatorDialog({
  open,
  onOpenChange,
  items,
  onJump,
  jumpEnabled,
}: SimulationQuestionNavigatorDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(calc(100dvh-2rem),560px)] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 px-4 pb-2 pt-4 sm:px-6 sm:pt-6">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ListChecks className="h-5 w-5 text-primary shrink-0" aria-hidden />
            {t("simulation.active.questionNavigatorTitle", {
              defaultValue: "Questions & progress",
            })}
          </DialogTitle>
          <DialogDescription className="text-left text-xs sm:text-sm">
            {t("simulation.active.questionNavigatorDescription", {
              defaultValue:
                "See which questions are unanswered or marked for review, and jump to them.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 [-webkit-overflow-scrolling:touch]">
          <ul
            className="space-y-1 px-2 pb-3 sm:px-4"
            role="listbox"
            aria-label={t("simulation.active.questionNavigatorTitle", {
              defaultValue: "Questions & progress",
            })}
          >
            {items.map((item) => {
              const answerLabel = item.isAnswered
                ? t("simulation.active.answered", { defaultValue: "Answered" })
                : t("simulation.active.unanswered", {
                    defaultValue: "Unanswered",
                  });
              const flaggedNote = item.isFlagged
                ? t("simulation.active.legend.flagged", {
                    defaultValue: "Flagged for review",
                  })
                : "";
              const ariaLabel = [
                item.label,
                answerLabel,
                flaggedNote,
                item.detail ?? "",
              ]
                .filter((s) => s.length > 0)
                .join(". ");
              return (
                <li key={item.id} role="listitem">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!jumpEnabled}
                    className={cn(
                      "w-full justify-start h-auto min-h-11 py-2.5 px-3 text-left font-normal",
                      !item.isAnswered &&
                        "border border-dashed border-muted-foreground/40 bg-muted/20",
                    )}
                    onClick={() => {
                      if (!jumpEnabled) return;
                      onJump(item.id);
                      onOpenChange(false);
                    }}
                    aria-label={ariaLabel}
                  >
                    <span className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2 min-w-0 w-full">
                      <span className="font-semibold text-foreground shrink-0">
                        {item.label}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] uppercase font-bold tracking-wide shrink-0",
                          item.isAnswered
                            ? "text-exam-success"
                            : "text-amber-600 dark:text-amber-500",
                        )}
                      >
                        {answerLabel}
                      </span>
                      {item.isFlagged ? (
                        <span className="text-[10px] uppercase font-bold tracking-wide shrink-0 rounded border border-exam-flag/30 bg-exam-flag/5 px-1.5 py-0.5 font-ui text-exam-flag">
                          {t("simulation.active.navigatorMarked", {
                            defaultValue: "Marked",
                          })}
                        </span>
                      ) : null}
                      {item.detail ? (
                        <span className="text-xs text-muted-foreground line-clamp-2 sm:ml-auto sm:text-right sm:max-w-[55%]">
                          {item.detail}
                        </span>
                      ) : null}
                    </span>
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="safe-area-bottom shrink-0 border-t border-border px-4 pt-2 sm:px-6 sm:pb-4">
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            {t("common.close", { defaultValue: "Close" })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
